import { Injectable } from '@angular/core';
import { filterNilValue, Order, QueryEntity } from '@datorama/akita';
import { Observable, switchMap } from 'rxjs';
import { distinctUntilChanged, filter, map, pluck, shareReplay } from 'rxjs/operators';
import { LegalEntity } from './legal-entity.model';
import { LegalEntityStore, LegalEntityState } from './legal-entity-store.service';
import { get } from 'dot-prop';
import {
    checkFilter,
    InferFilter,
    flattenNestedObject
} from 'src/app/shared/state/query-filter.functions';
import { LegalEntityService } from './legal-entity.service';

@Injectable({ providedIn: 'root' })
export class LegalEntityQuery extends QueryEntity<LegalEntityState> {
    constructor(
        protected store: LegalEntityStore,
        private _ownersService: LegalEntityService
    ) {
        super(store);
        this._ownersService.get().subscribe();
    }

    get all(): LegalEntity[] {
        return this.getAll();
    }
    public all$ = this.selectAll();
    public isLoading$ = this.selectLoading();

    public allAsObject$ = this.isLoading$.pipe(
        filter((isLoading) => !isLoading),
        switchMap(() => this.selectAll({ asObject: true })),
        shareReplay(1)
    );

    getOwnerById(id: LegalEntity['ownerId']): LegalEntity {
        return this.getEntity(id);
    }

    selectOwnerById$ = (id: LegalEntity['ownerId']) =>
        this.selectEntity(id).pipe(filterNilValue());

    selectOwnersByFilter$(
        filters: InferFilter<LegalEntity> = {},
        sortBy: keyof LegalEntity | any = 'accountShortName',
        sortByOrder: Order = Order.ASC
    ): Observable<LegalEntity[]> {
        return this.selectAll({
            sortBy,
            sortByOrder,
            filterBy: (owner) => {
                // ...flatten nested object and filter out null values
                const FILTERS = Object.entries<any>(flattenNestedObject(filters)).filter(
                    (filter) => filter[1] !== null
                );
                if (FILTERS.length > 0) {
                    return !FILTERS.find(([key, criteria]) => {
                        const value = get<string | number>(owner, key);
                        return !checkFilter(value, criteria);
                    });
                } else {
                    return true;
                }
            }
        });
    }

    /** active$
     * @description Observable for the active (selected) legal entities
     * @returns Observable<LegalEntity[]>
     */
    active$ = this.selectActive().pipe(
        map((legalEntities) => {
            return legalEntities.filter(
                (legalEntity) => legalEntity.metadata?.includeInGlobalFilter !== false
            );
        })
    );

    /** hasActive
     * @description Check if at least one legal entity is active (has been selected)
     * @returns boolean
     */
    hasActive$ = this.active$.pipe(
        map((active) => active.length > 0),
        shareReplay(1)
    );

    /** isFiltered$
     * @description Check if legal entities are filtered, i.e. if the number of active legal entities is different from the total number of legal entities that are not excluded from the global filter
     * @returns boolean
     */
    isFiltered$ = this.select(
        (state) =>
            state?.active.length !==
            Object.values(state?.entities || {}).filter(
                (legalEntity) => legalEntity.metadata?.includeInGlobalFilter !== false
            ).length
    ).pipe(distinctUntilChanged(), shareReplay(1));

    isSelected(id: LegalEntity['ownerId']): boolean {
        return this.hasActive(id);
    }

    isLoading(request: string): Observable<boolean> {
        return this.select('ui').pipe(pluck(request), pluck('isLoading'));
    }
}
